"use client";

import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type RefObject,
} from "react";

import { CC_COUNT_UP_DURATION_MS } from "@/lib/cc-count-up";
import { useCcPrefersReducedMotion } from "@/lib/use-cc-prefers-reduced-motion";

export const CC_DIGIT_ROLL_STAGGER_MS = 120;
export const CC_DIGIT_ROLL_IN_VIEW_THRESHOLD = 0.5;

function readSkipAnimation(sessionStorageKey?: string) {
  if (!sessionStorageKey || typeof window === "undefined") {
    return false;
  }
  try {
    return window.sessionStorage.getItem(sessionStorageKey) === "1";
  } catch {
    return false;
  }
}

function markAnimationPlayed(sessionStorageKey?: string) {
  if (!sessionStorageKey) return;
  try {
    window.sessionStorage.setItem(sessionStorageKey, "1");
  } catch {
    /* private mode / blocked storage */
  }
}

export type UseCcDigitRollInViewOptions = {
  /** When false, the observer waits until this becomes true (e.g. active tab panel). */
  enabled?: boolean;
  threshold?: number;
  /** If set, skip animation after the first successful play in this tab session. */
  sessionStorageKey?: string;
  /** Number of digit columns (for finish timing). */
  digitCount: number;
  digitStaggerMs?: number;
};

export type UseCcDigitRollInViewResult = {
  rootRef: RefObject<HTMLSpanElement | null>;
  showFinal: boolean;
  animate: boolean;
};

/**
 * Intersection-triggered digit roll state (404 SEO stats, demo KPIs).
 * SSR/hydration: starts on final values; animation runs after mount when in view.
 */
export function useCcDigitRollInView({
  enabled = true,
  threshold = CC_DIGIT_ROLL_IN_VIEW_THRESHOLD,
  sessionStorageKey,
  digitCount,
  digitStaggerMs = CC_DIGIT_ROLL_STAGGER_MS,
}: UseCcDigitRollInViewOptions): UseCcDigitRollInViewResult {
  const rootRef = useRef<HTMLSpanElement>(null);
  const prefersReducedMotion = useCcPrefersReducedMotion();
  const skipAnimationRef = useRef(false);
  const hasPlayedRef = useRef(false);
  const [showFinal, setShowFinal] = useState(true);
  const [animate, setAnimate] = useState(false);

  useLayoutEffect(() => {
    const skip =
      prefersReducedMotion || readSkipAnimation(sessionStorageKey);
    skipAnimationRef.current = skip;
    if (skip) {
      setShowFinal(true);
      setAnimate(false);
    }
  }, [prefersReducedMotion, sessionStorageKey]);

  useEffect(() => {
    if (
      !enabled ||
      prefersReducedMotion ||
      skipAnimationRef.current ||
      hasPlayedRef.current
    ) {
      return;
    }

    const root = rootRef.current;
    if (!root) {
      return;
    }

    let finishTimer = 0;

    const startAnimation = () => {
      if (hasPlayedRef.current) return;
      hasPlayedRef.current = true;

      setShowFinal(false);
      requestAnimationFrame(() => {
        setAnimate(true);

        const lastDelay =
          Math.max(0, digitCount - 1) * digitStaggerMs + CC_COUNT_UP_DURATION_MS;
        finishTimer = window.setTimeout(() => {
          markAnimationPlayed(sessionStorageKey);
          setAnimate(false);
          setShowFinal(true);
        }, lastDelay + 50);
      });
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) {
          return;
        }
        observer.disconnect();
        startAnimation();
      },
      { threshold },
    );

    observer.observe(root);

    return () => {
      observer.disconnect();
      if (finishTimer) {
        window.clearTimeout(finishTimer);
      }
    };
  }, [
    enabled,
    prefersReducedMotion,
    sessionStorageKey,
    threshold,
    digitCount,
    digitStaggerMs,
  ]);

  return { rootRef, showFinal, animate };
}
